import { Typography, Tooltip, Stack, Divider } from '@mui/material';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
  Icon,
  IconContainer,
  ItemText,
  ItemTextContainer,
  ItemValuePanel,
  StyledListItem,
} from './styles';
import { IotListItemProps } from './types';
import { IotLockIcon, IotLockdownIcon, IotUnlockIcon } from 'src/components/iot/icons';
import { IotItem } from '../../types';

export const IotListItem: FC<IotListItemProps & { children?: any }> = ({
  item,
  settings,
  compact,
  key
}) => {
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const [sensor, setSensor] = useState<IotItem>();
  const [engine, setEngine] = useState<Record<string, any> | undefined>();

  const isThingworxItem = item.type === 'thing';

  const alertColor = item.alert?.lastStatus === 'alarm' ? '#8E505D' :
                     item.alert?.lastStatus === 'warning' ? '#99733B' :
                     null;

  const lastMeasured = isThingworxItem && item?.additionalInfo?.LastMeasured
    ? moment(item.additionalInfo.LastMeasured).format('DD MMM YYYY, hh:mm A')
    : 'N/A';

  const checkIfClamped = () => {
    const element = textRef.current;
    if (element) {
      const isOverflowing = element.scrollHeight > element.clientHeight;
      setIsClamped(isOverflowing);
    }
  };

  //set sensor as item
  useEffect(() => {
    const s = item;


    let sensorValue = s?.value;
    if (s.additionalInfo) {
      sensorValue = s.additionalInfo[s.additionalInfo.PropertyName];
    }

    //if item.value looks like a float number, then parse to float with 2 decimal places
    if (sensorValue === 0 || !isNaN(parseFloat(sensorValue?.toString()))) {
      s.value = parseFloat(sensorValue.toString()).toFixed(2);
      s.valueType = 'number';
    } else if (sensorValue !== undefined && sensorValue !== null) {
      s.value = sensorValue.toString();
      s.valueType = 'string';
    }
    setSensor(s);
  }, [item, item?.value]);

  //set engine as item
  useEffect(() => {
    if (item.type === 'engine') {
      const info = item?.additionalInfo;
      if (!info) return;
      const value =
        typeof info?.Value === 'number' ? Math.floor(info?.Value * 100) / 100 : info?.Value;

      setEngine({ ...info, Value: value });
    }
  }, [item, item?.additionalInfo?.Value]);

  useLayoutEffect(() => {
    checkIfClamped();
  });

  return (
    <>
      {key !==0 && (
        <Divider sx={{ width: '100%', mt:'5px', mb: '5px' }} />
      )}
      <StyledListItem data-tooltip-id="my-tooltip-1">
      <IconContainer
        size={settings.iconSize}
        background={alertColor}
        style={{
          marginRight: `${settings.itemContentSpacing}px`,
          borderRadius: `${settings.iconBorderRadius}px`,
          flexShrink: 0,
        }}
      >
        {sensor?.relatedMeasureUnit?.icon ? (
          <Icon
            style={{ width: `75%`, height: `75%` }}
            src={`${process.env.NEXT_PUBLIC_API}/v1/public/icons/${sensor?.relatedMeasureUnit?.icon}?hex=%23FFFFFF`}
            alt=""
          />
        ) : (item.type === 'sensor' || isThingworxItem) ? (
          <i className="fa-regular fa-gauge-high" />
        ) : (
          <i className="fa-regular fa-door-open" />
        )}
      </IconContainer>
      <ItemTextContainer style={{ flex: compact ? 'none' : 1 }}>
        <Tooltip title={sensor?.name || sensor?.externalDeviceId || ''} placement="top">
          <Stack sx={{display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <ItemText
            ref={textRef}
            lineClamp={settings.lineClamp}
            isTitle
            style={{
              wordBreak: 'break-word',
              lineHeight: '1.2em',
              paddingBottom: '2px'
            }}
          >
            {sensor?.additionalInfo?.MeasurementType ||sensor?.name || sensor?.externalDeviceId}
          </ItemText>
          <Typography sx={{ fontSize: '12px', opacity: 0.7 }}>
            {lastMeasured}
          </Typography>
          </Stack>

        </Tooltip>
        {sensor?.measureName && (
          <Tooltip title={isClamped ? sensor?.measureName : ''} placement="top">
            <ItemText
              ref={textRef}
              lineClamp={settings.lineClamp}
              maxWidth={settings.measureNameMaxWidth}
            >
              {sensor?.measureName}
            </ItemText>
          </Tooltip>
        )}
      </ItemTextContainer>

      {item?.type === 'sensor' ? (
        <ItemValuePanel style={{ marginLeft: `${settings.itemContentSpacing}px` }}>
          <Tooltip title={sensor?.valueType === 'number' ? sensor?.value : ''} placement="top">
            <Typography sx={{ fontSize: '14px' }}>
            {sensor?.value === undefined || sensor?.value === null
                ? 'Fetching data...'
                : sensor?.valueType === 'number'
                ? `${sensor?.value} ${
                    sensor?.relatedMeasureUnit ? sensor?.relatedMeasureUnit?.symbol : ''
                  }`
                : sensor?.value}
            </Typography>
          </Tooltip>
        </ItemValuePanel>
      ) : item.type === 'engine' ? (
        <Stack>
          <ItemValuePanel style={{ marginLeft: `${settings.itemContentSpacing}px` }}>
            {!!engine && (
              <Typography sx={{ fontSize: '14px', mr: 1 }}>{`Value: ${engine?.Value}`}</Typography>
            )}
          </ItemValuePanel>
          <ItemValuePanel style={{ marginLeft: `${settings.itemContentSpacing}px` }}>
            {!!engine && (
              <Typography
                sx={{ fontSize: '14px', mr: 1 }}
              >{`Quality: ${engine?.Quality}`}</Typography>
            )}
          </ItemValuePanel>
        </Stack>
      ) : item.type === 'thing' ? (
          <ItemValuePanel background={alertColor} style={{ marginLeft: `${settings.itemContentSpacing}px` }}>
            <Typography sx={{
              fontSize: '14px',
              mr: 1,
            }}>
            {sensor?.value === undefined || sensor?.value === null
                ? 'Fetching data...'
                : sensor?.valueType === 'number'
                  ? `${sensor?.value} ${
                      sensor?.relatedMeasureUnit ? sensor?.relatedMeasureUnit?.symbol : ''
                    }`
                  : sensor?.value}
            </Typography>
          </ItemValuePanel>
      ) : (
        <ItemValuePanel
          onClick={sensor?.onClick}
          style={{ marginLeft: `${settings.itemContentSpacing}px` }}
        >
          {sensor?.lockedDown ? (
            <>
              <Typography sx={{ fontSize: '14px', mr: 1 }}>Locked down</Typography>
              <IotLockdownIcon />
            </>
          ) : sensor?.unlocked ? (
            <>
              <Typography sx={{ fontSize: '14px', mr: 1 }}>Open</Typography>
              <IotUnlockIcon />
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: '14px', mr: 1 }}>Locked</Typography>
              <IotLockIcon />
            </>
          )}
        </ItemValuePanel>
      )}
    </StyledListItem>
    </>
  );
};
